
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileText, ImageIcon, Loader2, Paperclip, Send, Check, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { NoteAttachmentGallery } from "@/components/ui/note-attachment-gallery";
import { ACCEPTED_TYPES_SET, MAX_FILE_SIZE } from "@/components/ui/file-upload";

const ACCEPTED_TYPES = "image/jpeg,image/png,image/gif,image/webp,application/pdf";
const MAX_FILES_DEFAULT = 5;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface NoteEditorProps {
  value: string;
  onChange: (value: string) => void;
  pendingFiles: File[];
  onPendingFilesChange: (files: File[]) => void;
  // noteId enables JIT attachment display via NoteAttachmentGallery (edit mode).
  // Omit for new notes (no existing attachments possible).
  noteId?: string;
  onDeleteAttachment?: (attachmentId: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  submitIcon?: "send" | "save";
  isSubmitting?: boolean;
  placeholder?: string;
  maxFiles?: number;
  autoFocus?: boolean;
}

export function NoteEditor({
  value,
  onChange,
  pendingFiles,
  onPendingFilesChange,
  noteId,
  onDeleteAttachment,
  onSubmit,
  onCancel,
  submitLabel = "Send",
  submitIcon = "send",
  isSubmitting = false,
  placeholder = "Add a note...",
  maxFiles = MAX_FILES_DEFAULT,
  autoFocus = false,
}: NoteEditorProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const existingCount = 0; // attachments counted from store via noteId if needed
  const effectiveMax = maxFiles - existingCount;
  const canAttach = pendingFiles.length < effectiveMax;
  const canSubmit = (!!value.trim() || pendingFiles.length > 0) && !isSubmitting;

  const addFiles = useCallback(
    (incoming: File[]) => {
      const valid = incoming.filter(
        (f) => ACCEPTED_TYPES_SET.has(f.type) && f.size <= MAX_FILE_SIZE
      );
      if (valid.length === 0) {
        if (incoming.length > 0)
          toast.error("Only images and PDFs up to 10 MB are supported.");
        return;
      }
      onPendingFilesChange([
        ...pendingFiles,
        ...valid.slice(0, Math.max(0, effectiveMax - pendingFiles.length)),
      ]);
    },
    [pendingFiles, onPendingFilesChange, effectiveMax]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      addFiles(Array.from(e.dataTransfer.files));
    },
    [addFiles]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const imageItems = items.filter((item) => item.type.startsWith("image/"));
      if (imageItems.length === 0) return;

      const files = imageItems
        .map((item) => {
          const blob = item.getAsFile();
          if (!blob) return null;
          const ext = blob.type.split("/")[1]?.replace("jpeg", "jpg") || "png";
          return new File([blob], `pasted-image-${Date.now()}.${ext}`, {
            type: blob.type,
          });
        })
        .filter(Boolean) as File[];

      addFiles(files);
    },
    [addFiles]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    onPendingFilesChange(pendingFiles.filter((_, i) => i !== index));
  };

  const SubmitIcon = submitIcon === "save" ? Check : Send;

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node))
          setIsDragOver(false);
      }}
      onDrop={handleDrop}
      className="relative rounded-lg border border-input bg-transparent shadow-xs transition-[border-color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 dark:bg-input/30"
    >
      {isDragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-primary/10 border-2 border-dashed border-primary pointer-events-none">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <ImageIcon className="h-5 w-5" />
            Drop files here
          </div>
        </div>
      )}

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        rows={2}
        autoFocus={autoFocus}
        onPaste={handlePaste}
        disabled={isSubmitting}
        onKeyDown={(e) => {
          if (e.key === "Escape" && onCancel) onCancel();
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && canSubmit) {
            e.preventDefault();
            onSubmit();
          }
        }}
        className="w-full resize-none border-0 bg-transparent px-3 pt-3 pb-1 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 field-sizing-content min-h-[3.5rem]"
      />

      {/* Existing attachments (edit mode) — JIT SAS resolution via store */}
      {noteId && (
        <div className="px-3 pb-1.5">
          <NoteAttachmentGallery
            noteId={noteId}
            onDelete={onDeleteAttachment}
          />
        </div>
      )}

      {/* Pending file chips */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pb-1.5">
          {pendingFiles.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-1.5 rounded-md border bg-muted/50 px-2 py-1 text-xs"
            >
              {file.type.startsWith("image/") ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="h-6 w-6 rounded object-cover"
                />
              ) : (
                <FileText className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="max-w-[120px] truncate">{file.name}</span>
              <span className="text-muted-foreground">
                {formatFileSize(file.size)}
              </span>
              <button
                type="button"
                className="ml-0.5 rounded-sm p-0.5 hover:bg-muted transition-colors"
                onClick={() => removeFile(i)}
                disabled={isSubmitting}
                aria-label={`Remove ${file.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Bottom toolbar */}
      <div className="flex items-center gap-2 border-t border-input/50 px-2 py-1.5">
        <button
          type="button"
          disabled={isSubmitting || !canAttach}
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Attach files"
          title="Attach files"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <span className="text-xs text-muted-foreground select-none">
          Drag &amp; drop or paste images
        </span>
        <div className="flex-1" />
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-3 text-xs"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit}
          onClick={onSubmit}
          className="h-7 gap-1.5 px-3 text-xs"
        >
          {isSubmitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <SubmitIcon className="h-3.5 w-3.5" />
          )}
          {submitLabel}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        className="hidden"
        onChange={handleFileSelect}
        aria-label="Upload files"
      />
    </div>
  );
}
